import PropTypes from 'prop-types';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { Button } from 'src/features/tyre-management/components/ui/button';
import SelectControl from 'src/features/tyre-management/components/SelectControl';
import { Label } from 'src/features/tyre-management/components/ui/label';
import { ALLOWED_PAGE_SIZES } from '../hooks/vehicleListQuery';

const pageList = (page, pageCount) => {
  if (pageCount <= 7) return Array.from({ length: pageCount }, (_, index) => index + 1);
  const pages = new Set([1, pageCount, page - 1, page, page + 1].filter((item) => item >= 1 && item <= pageCount));
  return Array.from(pages).sort((left, right) => left - right);
};

const VehicleListPagination = ({
  page, pageSize, count, onPageChange, onPageSizeChange,
}) => {
  const total = count || 0;
  const pageCount = Math.max(1, Math.ceil(total / pageSize));
  const pages = pageList(page, pageCount);
  const from = total === 0 ? 0 : ((page - 1) * pageSize) + 1;
  const to = Math.min(page * pageSize, total);

  return (
    <div className="flex flex-col gap-3 border-t border-slate-200 px-6 py-4 sm:flex-row sm:items-center sm:justify-between">
      <div className="flex flex-wrap items-center gap-3">
        <p className="text-sm text-slate-500">
          {total ? `${from}–${to} of ${total}` : '0 vehicles'}
        </p>
        <div className="flex items-center gap-2">
          <Label htmlFor="vl-page-size" className="text-xs text-slate-500">Per page</Label>
          <SelectControl
            id="vl-page-size"
            className="w-24"
            value={pageSize}
            onChange={(event) => onPageSizeChange(Number(event.target.value))}
          >
            {ALLOWED_PAGE_SIZES.map((size) => (
              <option key={size} value={size}>{size}</option>
            ))}
          </SelectControl>
        </div>
      </div>
      <nav className="flex items-center gap-1" aria-label="Pagination">
        <Button
          type="button"
          size="sm"
          variant="outline"
          disabled={page <= 1}
          onClick={() => onPageChange(page - 1)}
        >
          <ChevronLeft className="h-4 w-4" />
          Previous
        </Button>
        {pages.map((item, index) => (
          <span key={item} className="hidden items-center sm:flex">
            {index > 0 && pages[index - 1] !== item - 1 && <span className="px-1 text-slate-400">…</span>}
            <Button
              type="button"
              size="sm"
              variant={item === page ? 'default' : 'ghost'}
              aria-current={item === page ? 'page' : undefined}
              onClick={() => onPageChange(item)}
            >
              {item}
            </Button>
          </span>
        ))}
        <Button
          type="button"
          size="sm"
          variant="outline"
          disabled={page >= pageCount}
          onClick={() => onPageChange(page + 1)}
        >
          Next
          <ChevronRight className="h-4 w-4" />
        </Button>
      </nav>
    </div>
  );
};

VehicleListPagination.propTypes = {
  page: PropTypes.number.isRequired,
  pageSize: PropTypes.number.isRequired,
  count: PropTypes.number,
  onPageChange: PropTypes.func.isRequired,
  onPageSizeChange: PropTypes.func.isRequired,
};

export default VehicleListPagination;
